import React, { useState, useCallback, useEffect } from 'react'
import { FilterPanel } from '../components/dashboard/FilterPanel'
import { ArticlesList } from '../components/articles/ArticlesList'
import { FilterState } from '../types'
import { useArticles } from '../hooks/api/useArticles'
import { useWebSocket } from '../hooks/useWebSocket'
import { newsApi, articleApi } from '../services/api'

const NewsSignalsPage: React.FC = () => {
  const [currentFilters, setCurrentFilters] = useState<FilterState | null>(null)
  const [isFetching, setIsFetching] = useState(false)
  const [currentSessionId, setCurrentSessionId] = useState<string | null>(null)
  const [filtersVisible, setFiltersVisible] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize] = useState(20)
  const [progressData, setProgressData] = useState({
    message: '',
    progress: 0,
    current_page: 0,
    total_pages: 0,
    articles_found: 0
  })

  // Get articles for the current session with pagination
  const { data: articleResponse, isLoading, refetch } = useArticles(currentSessionId, currentPage, pageSize)
  const articles = articleResponse?.data || []
  const pagination = articleResponse?.pagination || { current_page: 1, page_size: 20, total_count: 0, total_pages: 1, has_next: false, has_prev: false }
  
  // WebSocket connection for real-time progress updates
  const { connected: wsConnected, lastMessage } = useWebSocket()

  // Debug logging
  useEffect(() => {
    console.log('Session ID changed:', currentSessionId)
    console.log('Articles count:', articles?.length || 0)
  }, [currentSessionId, articles])

  // Reset page when session changes
  useEffect(() => {
    if (currentSessionId) {
      setCurrentPage(1)
    }
  }, [currentSessionId])

  // Handle WebSocket progress updates
  useEffect(() => {
    if (lastMessage && lastMessage.type === 'progress_update' && lastMessage.session_id === currentSessionId) {
      const data = lastMessage.data
      setProgressData({
        message: data.message || '',
        progress: data.progress || 0,
        current_page: data.current_page || 0,
        total_pages: data.total_pages || 0,
        articles_found: data.articles_found || 0
      })
      
      // Auto-refresh articles when page progress is made (only for current page)
      if (data.current_page > 0 && currentPage === 1) {
        refetch()
      }
      
      // Stop fetching when completed or failed
      if (data.status === 'completed' || data.status === 'failed') {
        setIsFetching(false)
        if (data.status === 'completed') {
          refetch() // Final refresh to get all articles
        }
      }
    }
  }, [lastMessage, currentSessionId, refetch, currentPage])

  const handleFiltersChange = useCallback((filters: FilterState) => {
    // Only update if filters actually changed to prevent infinite loops
    setCurrentFilters(prevFilters => {
      const hasChanged = JSON.stringify(prevFilters) !== JSON.stringify(filters)
      return hasChanged ? filters : prevFilters
    })
  }, [])

  const handleFetchNews = useCallback(async () => {
    if (!currentFilters) {
      console.warn('No filters set for fetching news')
      return
    }
    
    setIsFetching(true)
    setProgressData({ message: 'Starting fetch...', progress: 0, current_page: 0, total_pages: 0, articles_found: 0 })
    
    // Auto-hide filters immediately when fetch starts
    setFiltersVisible(false)
    
    try {
      console.log('Fetching news with filters:', currentFilters)
      
      // Call the backend to fetch new articles with session
      const response = await newsApi.fetchArticles(currentFilters)
      
      console.log('Fetch response:', response)
      
      if (response.session_id) {
        console.log('Setting session ID:', response.session_id)
        setCurrentSessionId(response.session_id)
        // Progress updates will come via WebSocket
      } else {
        console.error('No session_id in response:', response)
        setIsFetching(false)
      }
    } catch (error) {
      console.error('Failed to fetch news:', error)
      // Reset session on error
      setCurrentSessionId(null)
      setIsFetching(false)
      setProgressData({ message: 'Error occurred', progress: 0, current_page: 0, total_pages: 0, articles_found: 0 })
    }
  }, [currentFilters])

  const handleExpandArticle = useCallback(async (articleId: string) => {
    try {
      console.log('Expanding article:', articleId)
      await articleApi.expandDescription(articleId)
      // Refetch articles to get updated status
      refetch()
    } catch (error) {
      console.error('Failed to expand article:', error)
    }
  }, [refetch])

  const handleSummarizeArticle = useCallback(async (articleId: string) => {
    try {
      console.log('Summarizing article:', articleId)
      await articleApi.summarizeArticle(articleId)
      // Refetch articles to get updated status
      refetch()
    } catch (error) {
      console.error('Failed to summarize article:', error)
    }
  }, [refetch])

  const handlePushToHubspot = useCallback(async (articleId: string) => {
    try {
      console.log('Pushing article to HubSpot:', articleId)
      await articleApi.pushToHubspot(articleId)
      // Refetch articles to get updated status
      refetch()
    } catch (error) {
      console.error('Failed to push to HubSpot:', error)
    }
  }, [refetch])

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Market Intelligence</h1>
        <p className="text-gray-600">
          Monitor and analyze mining industry news to identify sales opportunities and market trends.
        </p>
      </div>

      {/* Filter Panel */}
      <FilterPanel 
        onFiltersChange={handleFiltersChange}
        onFetchNews={handleFetchNews}
        isLoading={isFetching}
        filtersVisible={filtersVisible}
        onToggleFiltersVisible={() => setFiltersVisible(!filtersVisible)}
      />

      {/* Articles Section */}
      <ArticlesList
        articles={articles}
        isLoading={isLoading || isFetching}
        onExpandArticle={handleExpandArticle}
        onSummarizeArticle={handleSummarizeArticle}
        onPushToHubspot={handlePushToHubspot}
        sessionId={currentSessionId}
        progressData={progressData}
        pagination={pagination}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
      />
    </div>
  )
}

export default NewsSignalsPage
